import type { ApiIdentity } from '$lib/api/types';

// Partition key for client-side payload caches (home SWR, app/namespace
// caches, per-app secondary caches). It is NOT an authorization source: the
// server authorizes every request against current authority. Its only job is
// to make sure a payload fetched under one identity/session/class is never
// rendered after a transition to another — e.g. a downgraded member whose
// next /me read returns user_class='guest' must not keep painting the
// member's cached namespaces, viewer caps or internals.

let key = '';
let generation = 0;
const clearers: Array<() => void> = [];

function authorityKey(identity: ApiIdentity): string {
  return JSON.stringify([
    identity.sub,
    identity.principal_id,
    identity.user_class,
    identity.state,
    identity.platform_admin,
    identity.preview_origin?.admin_sub ?? null,
    identity.userNamespaceStub,
    identity.memberships.map((m) => [m.namespace_id, [...m.roles].sort()]),
    [...identity.groups].sort(),
  ]);
}

/**
 * Record the current identity. Every change, including initial adoption,
 * bumps the generation and synchronously clears registered caches: requests
 * started before authority resolved may have read a different server class.
 */
export function setCacheAuthority(identity: ApiIdentity | null): void {
  const next = identity ? authorityKey(identity) : '';
  if (next === key) return;
  key = next;
  generation++;
  for (const clear of clearers) clear();
}

/** Monotonic partition id; in-flight fetches compare it before seeding caches. */
export function authorityGeneration(): number {
  return generation;
}

/** Register a cache to drop on every authority transition. */
export function registerAuthorityClear(clear: () => void): void {
  clearers.push(clear);
}
